import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useQuery } from '@tanstack/react-query';
import FinancialService from '@frontend/AppSettings/services/FinancialService';
import { format, subDays, startOfDay, endOfDay, eachDayOfInterval, eachWeekOfInterval, endOfWeek } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { isEngineeringEnvironment } from '@frontend/AppSettings/environmentConfig';
import './FinancialSnapshotWidget.css';
import { normalizeToArray } from '../../helpers/responseHelpers';

/** Parse time_recorded from API (decimal hours string or "H:MM:SS" / "H:MM"). */
function parseTimeRecordedToHours(value: any): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const s = String(value).trim();
  if (s.includes(':')) {
    const parts = s.split(':').map((p) => parseFloat(p) || 0);
    const [h = 0, m = 0, sec = 0] = parts;
    return h + m / 60 + sec / 3600;
  }
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface FinancialSnapshotWidgetProps {
  className?: string;
}

type TimePeriod = 'Last 7 Days' | 'Last 30 Days' | 'Last 90 Days';

/**
 * FinancialSnapshotWidget Component
 * 
 * Displays a bar chart showing financial data over time:
 * - Time Recorded (hours)
 * - Unbilled Time (dollar amount)
 * - Outstanding (dollar amount)
 * 
 * Features:
 * - Fetches financial data from FinancialService
 * - Groups data by week
 * - Displays summary cards with key metrics
 * - Time period selector
 */
const FinancialSnapshotWidget: React.FC<FinancialSnapshotWidgetProps> = ({
  className = '',
}) => {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('Last 30 Days');

  // Fetch all billed time data
  const { data: billedTimeData, isPending, error } = useQuery({
    queryKey: ['Financial', 'billedTime', 'dashboard'],
    queryFn: () => FinancialService.getAllBilledTime(),
  });

  // Fetch all assigned fixed fees
  const { data: fixedFeesData } = useQuery({
    queryKey: ['Financial', 'fixedFees', 'dashboard'],
    queryFn: () => FinancialService.getAllAssignedFinancialData(),
  });

  // Normalize billed time entries to a consistent array shape.
  const billedEntries: any[] = normalizeToArray(billedTimeData);
  // Fixed fees API returns array of matters, each with fixed_fees[] where each fee has "fees" (not "amount")
  const fixedFeesList: any[] = normalizeToArray(fixedFeesData);
  const fixedFeesTotal = fixedFeesList
    .flatMap((m: any) => m.fixed_fees || [])
    .reduce((sum: number, f: any) => sum + (parseFloat(f?.fees) || 0), 0);

  // Process financial data by exact day periods
  const weeklyData = useMemo(() => {
    if (!billedEntries || billedEntries.length === 0) {
      return {
        weeks: [],
        timeRecorded: [],
        unbilledTime: [],
        outstanding: [],
      };
    }

    const now = new Date();
    const endDate = endOfDay(now);
    
    // Calculate exact number of days based on period
    const daysBack = timePeriod === 'Last 7 Days' ? 7 : timePeriod === 'Last 30 Days' ? 30 : 90;
    const startDate = startOfDay(subDays(now, daysBack - 1)); // Subtract daysBack - 1 to include today
    
    // Determine grouping strategy based on period
    const useDailyGrouping = timePeriod === 'Last 7 Days';
    
    let periods: Array<{ start: Date; end: Date; label: string }> = [];
    
    if (useDailyGrouping) {
      // For 7 days, group by individual days
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      periods = days.map((day) => ({
        start: startOfDay(day),
        end: endOfDay(day),
        label: format(day, 'MMM d'),
      }));
    } else {
      // For 30 and 90 days, group by weeks for better readability
      const weeks = eachWeekOfInterval(
        { start: startDate, end: endDate },
        { weekStartsOn: 1 }
      );
      
      periods = weeks.map((weekStart) => {
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        // Ensure week end doesn't exceed the end date
        const actualEnd = weekEnd > endDate ? endDate : weekEnd;
        return {
          start: startOfDay(weekStart),
          end: endOfDay(actualEnd),
          label: format(weekStart, 'MMM d'),
        };
      });
    }

    const periodStats = periods.map((period) => {
      let timeRecorded = 0;
      let unbilledTime = 0;
      let outstanding = 0;

      // Process billed time entries (API returns date_recorded; some code paths use date)
      billedEntries.forEach((entry: any) => {
        const dateValue = entry.date_recorded ?? entry.date;
        const entryDate = dateValue
          ? (typeof dateValue === 'string' ? new Date(dateValue) : dateValue)
          : null;
        
        if (entryDate) {
          const entryDateOnly = startOfDay(entryDate);
          
          // Check if entry falls within the period (inclusive of both start and end)
          if (entryDateOnly >= period.start && entryDateOnly <= period.end) {
            // Convert time_recorded to hours (API may send number or "H:MM:SS" string)
            const hours = parseTimeRecordedToHours(entry.time_recorded);
            
            timeRecorded += hours;
            
            // If not invoiced, add to unbilled time
            if (!entry.invoiceid) {
              unbilledTime += parseFloat(entry.amount || 0);
            } else {
              // If invoiced but not paid, add to outstanding
              outstanding += parseFloat(entry.amount || 0);
            }
          }
        }
      });

      return {
        period: period.label,
        timeRecorded,
        unbilledTime,
        outstanding,
      };
    });

    return {
      weeks: periodStats.map((s) => s.period),
      timeRecorded: periodStats.map((s) => s.timeRecorded),
      unbilledTime: periodStats.map((s) => s.unbilledTime),
      outstanding: periodStats.map((s) => s.outstanding),
    };
  }, [billedEntries, timePeriod]);

  // Calculate summary totals
  const summary = useMemo(() => {
    const totalTimeRecorded = weeklyData.timeRecorded.reduce((sum, val) => sum + val, 0);
    const totalUnbilledTime = weeklyData.unbilledTime.reduce((sum, val) => sum + val, 0);
    const totalOutstanding = weeklyData.outstanding.reduce((sum, val) => sum + val, 0) + fixedFeesTotal;

    // Count matters/projects with unbilled time
    const mattersWithUnbilled = new Set(
      billedEntries.filter((entry: any) => !entry.invoiceid).map((e: any) => e.matterid)
    ).size;

    // Count overdue invoices (simplified - would need invoice data)
    const overdueCount = 0; // Placeholder

    return {
      timeRecorded: totalTimeRecorded,
      unbilledTime: totalUnbilledTime,
      outstanding: totalOutstanding,
      mattersWithUnbilled,
      overdueCount,
    };
  }, [weeklyData, billedEntries, fixedFeesTotal]);

  // Prepare bar chart data
  const chartData = {
    labels: weeklyData.weeks,
    datasets: [
      {
        label: 'Time Recorded',
        data: weeklyData.timeRecorded,
        backgroundColor: '#007bff',
        borderColor: '#007bff',
        borderWidth: 1,
        yAxisID: 'y',
      },
      {
        label: 'Unbilled Time',
        data: weeklyData.unbilledTime,
        backgroundColor: '#fd7e14',
        borderColor: '#fd7e14',
        borderWidth: 1,
        yAxisID: 'y1',
      },
      {
        label: 'Outstanding',
        data: weeklyData.outstanding,
        backgroundColor: '#dc3545',
        borderColor: '#dc3545',
        borderWidth: 1,
        yAxisID: 'y1',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y || 0;
            if (datasetLabel === 'Time Recorded') {
              return `${datasetLabel}: ${value.toFixed(1)} hours`;
            } else {
              return `${datasetLabel}: $${value.toFixed(2)}`;
            }
          },
        },
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Time (Hours)',
        },
        ticks: {
          stepSize: 20,
          callback: function(value: any) {
            return value + 'h';
          },
        },
        beginAtZero: true,
      },
      y1: {
        type: 'linear' as const,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Amount (CAD $)',
        },
        ticks: {
          stepSize: 10000,
          callback: function(value: any) {
            const val = typeof value === 'number' ? value : parseFloat(value);
            if (val >= 1000) {
              return '$' + (val / 1000).toFixed(0) + 'K';
            }
            return '$' + val.toFixed(0);
          },
        },
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  if (isPending) {
    return (
      <div className={`financial-snapshot-widget ${className}`}>
        <div className="financial-snapshot-header">
          <h3 className="financial-snapshot-title">Financial Snapshot</h3>
        </div>
        <div className="financial-snapshot-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`financial-snapshot-widget ${className}`}>
        <div className="financial-snapshot-header">
          <h3 className="financial-snapshot-title">Financial Snapshot</h3>
        </div>
        <div className="financial-snapshot-error">Error loading data</div>
      </div>
    );
  }

  return (
    <div className={`financial-snapshot-widget ${className}`}>
      <div className="financial-snapshot-header">
        <h3 className="financial-snapshot-title">Financial Snapshot</h3>
        <select
          className="financial-snapshot-period-select"
          value={timePeriod}
          onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
        >
          <option value="Last 7 Days">Last 7 Days</option>
          <option value="Last 30 Days">Last 30 Days</option>
          <option value="Last 90 Days">Last 90 Days</option>
        </select>
      </div>

      <div className="financial-snapshot-content">
        {/* Bar Chart */}
        <div className="financial-snapshot-chart-container">
          <Bar data={chartData} options={chartOptions} />
        </div>

        {/* Summary Cards */}
        <div className="financial-snapshot-summary">
          <div className="financial-snapshot-summary-card time-recorded">
            <div className="financial-snapshot-summary-label">Time Recorded</div>
            <div className="financial-snapshot-summary-value">
              {summary.timeRecorded.toFixed(1)}
            </div>
          </div>

          <div className="financial-snapshot-summary-card unbilled-time">
            <div className="financial-snapshot-summary-label">Unbilled Time</div>
            <div className="financial-snapshot-summary-value">
              ${summary.unbilledTime.toFixed(0)}
            </div>
            <div className="financial-snapshot-summary-meta">
              {summary.mattersWithUnbilled} {isEngineeringEnvironment ? 'projects' : 'matters'}
            </div>
          </div>

          <div className="financial-snapshot-summary-card outstanding">
            <div className="financial-snapshot-summary-label">Outstanding</div>
            <div className="financial-snapshot-summary-value">
              ${summary.outstanding.toFixed(0)}
            </div>
            <div className="financial-snapshot-summary-meta">
              {summary.overdueCount > 0 && (
                <span className="financial-snapshot-overdue">
                  <FontAwesomeIcon icon={faCircle} />
                  {summary.overdueCount} overdue
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialSnapshotWidget;

